import * as XLSX from 'xlsx';
import type { DetectedStructure, RawRows } from './types';
import { parseCsv } from './csvParser';
import { findBestSheet, scoreSheets } from './findBestSheet';
import { findHeaderRow } from './findHeaderRow';
import { isTotalRow } from './isTotalRow';
import { findNameColumns } from './findNameColumns';

// Step 1 entry point: read a File, pick the best sheet (xlsx), detect the header
// row + name columns, and validate that there is usable data. Throws on the
// Functional Spec §3.7 error conditions (Step1Upload catches and renders them).
//
// `rawRows` in the returned structure is the FULL raw matrix (metadata + header +
// data). Header offset and total-row filtering are applied by consumers; this
// function uses the filtered data count only to validate "no data rows".

function hasContent(row: string[]): boolean {
	return row.some((c) => String(c).trim() !== '');
}

function dataRowsAfterHeader(rawRows: RawRows, headerRowIndex: number): RawRows {
	return rawRows
		.slice(headerRowIndex + 1)
		.filter((r) => hasContent(r) && !isTotalRow(r));
}

// `sheetName` (ENG-383): when provided (and present in the workbook), parse that
// sheet instead of the auto-detected best sheet. Used by the Step 2 sheet switcher.
// Ignored for CSV (no sheets).
export async function loadFile(
	file: File,
	sheetName?: string
): Promise<DetectedStructure> {
	const ext = (file.name.split('.').pop() || '').toLowerCase();

	let rawRows: RawRows;
	let sheets: Array<{ name: string; score: number }> = [];
	let selectedSheet: string | null = null;

	if (ext === 'csv') {
		rawRows = await parseCsv(file);
	} else if (ext === 'xlsx' || ext === 'xls') {
		const arrayBuffer = await file.arrayBuffer();
		const data = new Uint8Array(arrayBuffer);
		const wb = XLSX.read(data, { type: 'array' });
		sheets = wb.SheetNames.length > 1 ? scoreSheets(wb) : [];
		selectedSheet =
			sheetName && wb.SheetNames.includes(sheetName)
				? sheetName
				: findBestSheet(wb);
		const ws = wb.Sheets[selectedSheet];
		const raw = XLSX.utils.sheet_to_json<unknown[]>(ws, {
			header: 1,
			raw: true,
			defval: '',
			blankrows: false,
		});
		rawRows = raw.map((r) =>
			(r as unknown[]).map((c) => (c == null ? '' : String(c)))
		);
	} else {
		throw new Error('Unsupported file type');
	}

	if (rawRows.length === 0) {
		throw new Error('File is empty');
	}

	const headerRowIndex = findHeaderRow(rawRows);
	const headerRow = rawRows[headerRowIndex] ?? [];
	const dataRows = dataRowsAfterHeader(rawRows, headerRowIndex);
	if (dataRows.length === 0) {
		throw new Error('No data rows found');
	}

	const nameColumnConfig = findNameColumns(headerRow);

	return {
		sheets,
		selectedSheet,
		rawRows,
		headerRowIndex,
		nameColumnConfig,
		splitNameDetected: nameColumnConfig?.type === 'combined',
	};
}
